import type { DbExecutor } from "../executor";
import { DEFAULT_LABOR_RATE_CENTS_PER_HOUR } from "../../engine/assembly";
import { ValidationError } from "./projectRepository";

/**
 * Org-ready application settings (spec §20A / §10). The desktop app is the
 * implicit 'default' organization; SaaS later scopes rows per organization
 * without a schema change. Values are LIVE estimating assumptions for
 * current, non-baselined planning — future baselines freeze approved
 * expectations, and manual overrides / fixed-dollar quotes always win over
 * generic company assumptions.
 */
export class SettingsRepository {
  constructor(private readonly db: DbExecutor) {}

  private async get(key: string): Promise<string | null> {
    const rows = await this.db.select<{ value: string }>(
      "SELECT value FROM app_settings WHERE org_id = 'default' AND key = ?",
      [key],
    );
    return rows[0]?.value ?? null;
  }

  private async set(key: string, value: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute(
      `INSERT INTO app_settings (org_id, key, value, updated_at) VALUES ('default', ?, ?, ?)
       ON CONFLICT (org_id, key) DO UPDATE SET value = excluded.value, updated_at = excluded.updated_at`,
      [key, value, now],
    );
  }

  /** Company estimating labor rate, integer cents per hour (default $25/hr). */
  async getLaborRateCentsPerHour(): Promise<number> {
    const raw = await this.get("labor_rate_cents_per_hour");
    const parsed = raw === null ? NaN : Number(raw);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : DEFAULT_LABOR_RATE_CENTS_PER_HOUR;
  }

  async setLaborRateCentsPerHour(cents: number): Promise<void> {
    if (!Number.isSafeInteger(cents) || cents <= 0) {
      throw new ValidationError(["The labor rate must be a positive dollar amount per hour."]);
    }
    await this.set("labor_rate_cents_per_hour", String(cents));
  }
}
